import React, { useEffect, useState } from "react";
import {
    CheckBoxOutlineBlankOutlined,
    CheckBoxOutlined,
    FileDownloadOutlined,
} from "@mui/icons-material";
import {
    Checkbox,
    FormControlLabel,
    IconButton,
    Tooltip,
    Box,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import moment from "moment";

import AppConfirmDialog from "components/Shared/AppConfirmDialog";
import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import DateRangeToggle from "components/Shared/DateRangeToggle";

import { useLazyListVehicleMaintenanceQuery } from "store/services/vehicleMaintenanceApi";
import { useLazyGetDDLVehicleListQuery } from "store/services/vehicleApi";

import { localizeDateShort } from "utils/dateFormats";
import { exportExcelFile } from "utils/exportExcelFile";
import { useAuth } from "hooks";

const INITIAL_FORM = {
    dateRange: [],
    vehicleId: null,
};

const ExportVehicleMaintenanceExcel = ({ isBusy = false }) => {
    const [open, setOpen] = useState(false);
    const [showVehicleFilter, setShowVehicleFilter] = useState(false);

    const { viewEntity } = useAuth();

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { errors },
    } = useForm({ defaultValues: INITIAL_FORM });

    // RTK Queries
    const [fetchMaintenance, { isLoading: downloading }] =
        useLazyListVehicleMaintenanceQuery();

    const [fetchVehicles, { data: vehicles, isLoading: loadingVehicles }] =
        useLazyGetDDLVehicleListQuery();

    // Load vehicles only when dialog opens
    useEffect(() => {
        if (!open) return;
        fetchVehicles({
            account: viewEntity?.entity?.account?._id,
            entity: viewEntity?.entity?._id,
        });
    }, [open, fetchVehicles, viewEntity]);

    const onDownload = async (form) => {
        const { dateRange, vehicleId } = form;

        if (!dateRange?.[0] || !dateRange?.[1]) {
            toast.error("Please select a valid date range.");
            return;
        }

        // Prepare query parameters
        const params = {
            active: "true",
            limit: 10000,
            ...(showVehicleFilter && vehicleId && { vehicleId: vehicleId._id }),
            fromDate: moment(dateRange[0]).format("YYYY-MM-DD"),
            toDate: moment(dateRange[1]).format("YYYY-MM-DD"),
        };

        try {
            const response = await fetchMaintenance(params);

            if (response.error) {
                toast.error("Failed to fetch maintenance records.");
                return;
            }

            const resp = response.data;

            if (!resp?.data || resp.data.length === 0) {
                toast.info("No maintenance records found for the selected filters.");
                return;
            }

            const sheetData = resp.data.map((row, i) => ({
                SN: i + 1,
                "Maintenance ID": row.maintenanceId || "N/A",
                "Vehicle ID": row?.vehicle?.vehicle_id || "N/A",
                "Maintenance Type": row.maintenanceType || "N/A",
                "Total Cost": row.totalCost || "N/A",
                "Condition": row.maintenanceCondition || "N/A",
                "Maintenance Date": localizeDateShort(row.maintenanceDate) || "N/A",
                "Next Due": localizeDateShort(row.nextMaintenanceDue) || "N/A",
                "Created Date": localizeDateShort(row.createdAt) || "N/A",
                "Parts Replaced":
                    row.partsReplaced
                        ?.map((p) => `${p.partName} (${p.partCost})`)
                        .join(", ") || "N/A",
            }));

            exportExcelFile({
                sheetData,
                fileName: `VehicleMaintenance_${moment().format("YYYYMMDD")}`,
            });

            if (resp.totalItems > resp.data.length) {
                toast.info(
                    `Downloaded ${resp.data.length} of ${resp.totalItems} records.`
                );
            } else {
                toast.success(`Excel downloaded successfully! ${resp.data.length} records exported.`);
            }
        } catch (err) {
            toast.error("Failed to download data.");
        } finally {
            onClose();
        }
    };

    const onClose = () => {
        setOpen(false);
        setShowVehicleFilter(false);
        reset(INITIAL_FORM);
    };

    return (
        <>
            <Tooltip title="Download Excel" arrow>
                <IconButton color="success" onClick={() => setOpen(true)}>
                    <FileDownloadOutlined />
                </IconButton>
            </Tooltip>

            <AppConfirmDialog
                open={open}
                onClose={onClose}
                onConfirm={handleSubmit(onDownload)}
                title="Download Vehicle Maintenance Excel"
                loading={downloading || isBusy}
                dialogIcon={() => <FileDownloadOutlined color="success" />}
                sx={{ "& .MuiDialog-paper": { maxWidth: 560, width: "100%" } }}
            >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 1 }}>
                    {/* Filter by Vehicle */}
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={showVehicleFilter}
                                onChange={(e) => {
                                    setShowVehicleFilter(e.target.checked);
                                    if (!e.target.checked) setValue("vehicleId", null);
                                }}
                                icon={<CheckBoxOutlineBlankOutlined />}
                                checkedIcon={<CheckBoxOutlined />}
                                size="small"
                            />
                        }
                        label="Filter by Vehicle"
                        sx={{ m: 0 }}
                    />

                    {showVehicleFilter && (
                        <AppForm loading={isBusy}>
                            <AppForm.Content>
                                <Controller
                                    name="vehicleId"
                                    control={control}
                                    rules={{ required: showVehicleFilter ? "Select a vehicle" : false }}
                                    render={({ field }) => (
                                        <BaseAutocomplete
                                            fullWidth
                                            label="Vehicle"
                                            required={showVehicleFilter}
                                            value={field.value || null}
                                            onChange={(_, v) => field.onChange(v)}
                                            getOptionLabel={(o) => o?.vehicle_id || ""}
                                            isOptionEqualToValue={(o, v) => o?._id === v?._id}
                                            options={vehicles || []}
                                            loading={loadingVehicles}
                                            loadingText="Loading vehicles..."
                                            helperText={errors.vehicleId?.message}
                                            error={!!errors.vehicleId}
                                            size="small"
                                        />
                                    )}
                                />
                            </AppForm.Content>
                        </AppForm>
                    )}

                    {/* Date Range */}
                    <Controller
                        name="dateRange"
                        control={control}
                        rules={{ required: "Date range is required" }}
                        render={({ field, fieldState }) => (
                            <DateRangeToggle
                                {...field}
                                label="Select Date Range"
                                size="small"
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                            />
                        )}
                    />
                </Box>
            </AppConfirmDialog>
        </>
    );
};

export default ExportVehicleMaintenanceExcel;